import { useRef, useState, type ReactNode } from "react";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  ClipboardCopy,
  Folder,
  KeyRound,
  Link2,
  Settings,
} from "lucide-react";
import { useOnboarding } from "@/hooks/use-onboarding";
import { useResolvedHost } from "@/hooks/use-resolved-host";
import {
  CLAUDE_PLUGIN_INSTALL_COMMANDS,
  CLAUDE_PLUGIN_SETTINGS_SNIPPET,
  CLAUDE_PLUGIN_SETUP_COMMAND,
  MCP_TOKEN_PLACEHOLDER,
  QUICK_START_SETUP_COMMANDS,
  getMcpConfigBlock,
} from "@/lib/chat/cli-guidance";
import { cn } from "@/lib/utils";
import { useScrollSpotlight } from "../scroll-spotlight";

import { CopyableTerminal } from "../copyable-terminal";

/* -------------------------------------------------------------------------- */
/*  Shared primitives (same pattern as cli-setup-steps)                       */
/* -------------------------------------------------------------------------- */

function NumberedInstruction({
  step,
  title,
  children,
}: {
  step: number;
  title: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);

  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex flex-col items-center">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">
          {step}
        </div>
        <div className="mt-3 w-px flex-1 bg-white/[0.06]" />
      </div>
      <div className="min-w-0 flex-1 pb-28">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function NumberedInstructionLast({
  step,
  title,
  children,
}: {
  step: number;
  title: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);

  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">
        {step}
      </div>
      <div className="min-w-0 flex-1 pb-12">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function InfoCard({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-5">
      {children}
    </div>
  );
}

function Troubleshooting({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-8 space-y-3 border-t border-white/[0.06] pt-6">
      <button
        type="button"
        onClick={() => {
          setOpen((current) => !current);
        }}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        Need help?
      </button>
      {open ? (
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">
          {children}
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  ConfigureMcpStep — host-aware dispatcher                                   */
/* -------------------------------------------------------------------------- */

/**
 * MCP setup differs by host. The Copilot CLI reads a hand-edited
 * `~/.copilot/mcp-config.json`; Claude Code installs the `spectree` plugin,
 * saves the token with `claude mcp add --scope local` (setup cannot create a
 * token, only keep one), then runs `/spectree:spectree-setup`, which moves that
 * entry onto the MCP launcher in the user's own `~/.claude.json` (no committable
 * `.mcp.json`). We render the matching flow for the user's preferred host
 * (A8b dual-host).
 */
export function ConfigureMcpStep() {
  const host = useResolvedHost();
  return host === "claude" ? <ClaudeConfigureMcpFlow /> : <CopilotConfigureMcpFlow />;
}

/* -------------------------------------------------------------------------- */
/*  Copilot flow — hand-edit ~/.copilot/mcp-config.json                        */
/* -------------------------------------------------------------------------- */

function CopilotConfigureMcpFlow() {
  const { selectedPlatform } = useOnboarding();
  const platform = selectedPlatform ?? "mac";
  const isMac = platform === "mac";
  const isWindowsCmd = platform === "windows-cmd";
  const terminalLabel = isMac ? "Terminal" : isWindowsCmd ? "Command Prompt" : "PowerShell";
  const promptSymbol = isMac ? "$ " : "> ";
  const [copiedConfig, setCopiedConfig] = useState(false);
  const [apiToken, setApiToken] = useState("");

  const trimmedApiToken = apiToken.trim();
  const hasValidApiToken = trimmedApiToken.startsWith("st_");
  const configBlock = getMcpConfigBlock(trimmedApiToken);

  const copilotFolderPath = isMac
    ? "~/.copilot"
    : isWindowsCmd
      ? "%USERPROFILE%\\.copilot"
      : "$env:USERPROFILE\\.copilot";
  const configPath = isMac
    ? "~/.copilot/mcp-config.json"
    : isWindowsCmd
      ? "%USERPROFILE%\\.copilot\\mcp-config.json"
      : "$env:USERPROFILE\\.copilot\\mcp-config.json";
  const openCommand = isMac
    ? "open ~/.copilot/mcp-config.json"
    : isWindowsCmd
      ? "notepad %USERPROFILE%\\.copilot\\mcp-config.json"
      : 'notepad "$env:USERPROFILE\\.copilot\\mcp-config.json"';
  const checkFolderCommand = isMac
    ? "ls -la ~/.copilot"
    : isWindowsCmd
      ? 'if exist "%USERPROFILE%\\.copilot" (echo True) else (echo False)'
      : 'Test-Path "$env:USERPROFILE\\.copilot"';
  const createFolderCommand = isMac
    ? "mkdir -p ~/.copilot"
    : isWindowsCmd
      ? 'if not exist "%USERPROFILE%\\.copilot" mkdir "%USERPROFILE%\\.copilot"'
      : 'New-Item -ItemType Directory -Path "$env:USERPROFILE\\.copilot" -Force';
  const createConfigCommand = isMac
    ? "touch ~/.copilot/mcp-config.json"
    : isWindowsCmd
      ? 'if not exist "%USERPROFILE%\\.copilot\\mcp-config.json" type nul > "%USERPROFILE%\\.copilot\\mcp-config.json"'
      : 'New-Item -ItemType File -Path "$env:USERPROFILE\\.copilot\\mcp-config.json" -Force';

  const copyConfig = async () => {
    await navigator.clipboard.writeText(configBlock);
    setCopiedConfig(true);
    setTimeout(() => { setCopiedConfig(false); }, 2000);
  };

  return (
    <div className="space-y-0">
      {/* ---- Step 1: What is MCP ---- */}
      <NumberedInstruction step={1} title="Understand what this step does">
        <p className="text-sm leading-7 text-muted-foreground">
          Right now, GitHub Copilot and SpecTree do not know how to talk to each other. This step adds the bridge between them. That bridge is called <strong className="text-foreground/90">MCP</strong> (Model Context Protocol).
        </p>
        <InfoCard>
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/[0.08] bg-blue-500/10 text-blue-400">
              <Link2 className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">
                Think of MCP as a phone line
              </p>
              <p className="mt-2 text-sm leading-7 text-muted-foreground">
                Without it, Copilot cannot ask SpecTree to do anything for you. Once you set it up, the two tools can pass messages back and forth.
              </p>
            </div>
          </div>
        </InfoCard>

        {/* Distinction callout */}
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/15 text-amber-400">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-200">
                This is not a VS Code setting
              </p>
              <p className="mt-1.5 text-sm leading-6 text-muted-foreground">
                The config file lives in a folder called{" "}
                <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs text-foreground/90">.copilot</code>{" "}
                in your home folder. It is only for the <strong className="text-foreground/90">Copilot CLI app</strong>. You will not find it in VS Code settings.
              </p>
            </div>
          </div>
        </div>
      </NumberedInstruction>

      {/* ---- Step 2: Reveal hidden files ---- */}
      <NumberedInstruction step={2} title="Reveal hidden files in your home folder">
        <p className="text-sm leading-7 text-muted-foreground">
          The <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">.copilot</code>{" "}
          folder starts with a dot, so your computer may hide it. Turn on hidden files first so this folder is easy to find.
        </p>

        <InfoCard>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/[0.08] bg-white/[0.06] text-muted-foreground/70">
                <Folder className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {isMac ? "macOS hidden-file shortcut" : "Windows hidden-file shortcut"}
                </p>
                <p className="mt-2 text-sm leading-7 text-muted-foreground">
                  {isMac
                    ? "Open Finder, go to your home folder, then press ⌘ + Shift + . to show hidden folders like .copilot. Press it again later if you want to hide them."
                    : "Open File Explorer, go to your user folder, then choose View → Show → Hidden items so folders like .copilot appear."}
                </p>
              </div>
            </div>
            <p className="text-xs leading-6 text-muted-foreground">
              {isMac
                ? "If you would rather use Terminal, hidden folders already show there."
                : `If you would rather use ${terminalLabel}, hidden folders already show there. The File Explorer setting only helps if you want to click to the folder.`}
            </p>
          </div>
        </InfoCard>
      </NumberedInstruction>

      {/* ---- Step 3: Check the .copilot folder ---- */}
      <NumberedInstruction step={3} title="Check whether the .copilot folder already exists">
        <p className="text-sm leading-7 text-muted-foreground">
          This folder lives in your home folder and stores the Copilot CLI MCP file. Copilot CLI often makes it for you the first time you use it, so it is normal if the folder is not there yet.
        </p>

        <InfoCard>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/[0.08] bg-white/[0.06]">
                <Folder className="h-4 w-4 text-muted-foreground/70" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground/60">
                  {isMac ? "macOS" : "Windows"} .copilot folder
                </p>
                <p className="mt-1.5 break-all font-mono text-sm text-foreground/90">
                  {copilotFolderPath}
                </p>
              </div>
            </div>

            <div className="border-t border-white/[0.06] pt-4">
              <p className="mb-2 text-xs font-semibold text-muted-foreground/80">
                Quick check:
              </p>
              <CopyableTerminal title={terminalLabel} copyText={checkFolderCommand}>
                <p>
                  <span className="text-foreground/45">{promptSymbol}</span>
                  {checkFolderCommand}
                </p>
              </CopyableTerminal>
              <p className="mt-2 text-xs text-muted-foreground">
                {isMac
                  ? "If you see a folder list, .copilot is already there. If you see a missing-folder message, make it with the next command."
                  : `If ${terminalLabel} prints True, the folder is already there. If it prints False, make it with the next command.`}
              </p>
            </div>
          </div>
        </InfoCard>

        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
          <p className="mb-2 text-sm font-semibold text-foreground">
            Missing the folder?
          </p>
          <p className="text-sm leading-6 text-muted-foreground">
            Make it once, then keep using the same spot for your MCP config file.
          </p>
          <div className="mt-4">
            <CopyableTerminal title="Create the .copilot folder" copyText={createFolderCommand}>
              <p>
                <span className="text-foreground/45">{promptSymbol}</span>
                {createFolderCommand}
              </p>
            </CopyableTerminal>
          </div>
        </div>
      </NumberedInstruction>

      {/* ---- Step 4: Generate API token ---- */}
      <NumberedInstruction step={4} title="Generate your SpecTree API token">
        <p className="text-sm leading-7 text-muted-foreground">
          An API token is like a one-time password for apps. Copilot uses it to prove it is acting for you when it talks to SpecTree. Here is how to make one.
        </p>

        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-500/15 text-[11px] font-bold text-violet-400">
              A
            </span>
            <div className="text-sm leading-7 text-muted-foreground">
              Open SpecTree in your browser and <strong className="text-foreground/90">sign in</strong>.
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-500/15 text-[11px] font-bold text-violet-400">
              B
            </span>
            <div className="text-sm leading-7 text-muted-foreground">
              Click on <strong className="text-foreground/90">Settings</strong> in the
              bottom dock. Look for the{" "}
              <span className="inline-flex items-center gap-1">
                <Settings className="inline h-3.5 w-3.5 text-muted-foreground/70" />
                <span className="font-medium text-foreground/90">gear icon</span>
              </span>.
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-500/15 text-[11px] font-bold text-violet-400">
              C
            </span>
            <div className="text-sm leading-7 text-muted-foreground">
              Click on the <strong className="text-foreground/90">API Tokens</strong> tab
              on the left side of the Settings page.
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-500/15 text-[11px] font-bold text-violet-400">
              D
            </span>
            <div className="text-sm leading-7 text-muted-foreground">
              Click <strong className="text-foreground/90">Generate New Token</strong>, give it a name like <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">Copilot CLI</code>, then click <strong className="text-foreground/90">Create</strong>.
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-500/15 text-[11px] font-bold text-violet-400">
              E
            </span>
            <div className="text-sm leading-7 text-muted-foreground">
              <strong className="text-foreground/90">Copy the token right away.</strong> It starts with <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">st_</code> and only shows once. If you close the page first, you will need to make a new one.
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-white/[0.08] bg-[#0D0D0F] px-4 py-3">
          <p className="mb-2 text-xs text-muted-foreground/60">Your token will look like this:</p>
          <p className="font-mono text-sm text-emerald-400/80">
            st_K7xH2mPqR5vN8sT1wY4zA6bC9dE0fG3hI...
          </p>
        </div>
      </NumberedInstruction>

      {/* ---- Step 5: Paste the config ---- */}
      <NumberedInstructionLast step={5} title="Paste the SpecTree config and save">
        <p className="text-sm leading-7 text-muted-foreground">
          The config file goes in the <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">.copilot</code>{" "}
          folder at <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">{configPath}</code>. Open that file, paste the JSON below, and save it.
        </p>

        <InfoCard>
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-xs font-semibold text-muted-foreground/80">
                Quickest way to open the config file:
              </p>
              <CopyableTerminal title={terminalLabel} copyText={openCommand}>
                <p>
                  <span className="text-foreground/45">{promptSymbol}</span>
                  {openCommand}
                </p>
              </CopyableTerminal>
            </div>

            <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/15 text-amber-400">
                  <AlertTriangle className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-amber-200">File does not exist? You need to create it first</p>
                  <div className="mt-1.5 space-y-2 text-sm leading-6 text-muted-foreground">
                    <p>
                      If you get an error saying the file was not found, that just means it has not been created yet. Run this command to create it, then try the open command above again:
                    </p>
                    <CopyableTerminal title={terminalLabel} copyText={createConfigCommand}>
                      <p>
                        <span className="text-foreground/45">{promptSymbol}</span>
                        {createConfigCommand}
                      </p>
                    </CopyableTerminal>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </InfoCard>

        <p className="text-sm leading-7 text-muted-foreground">
          Paste your token above first so the copied JSON is ready to use.
        </p>

        <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-4">
          <label
            htmlFor="spectree-api-token"
            className="text-sm font-medium text-foreground/90"
          >
            Paste your SpecTree API token
          </label>
          <div
            className={cn(
              "mt-3 flex items-center gap-3 rounded-lg border bg-white/[0.04] px-3.5 py-3 transition-all focus-within:border-violet-500/40 focus-within:ring-1 focus-within:ring-violet-500/30",
              hasValidApiToken ? "border-emerald-500/30 bg-white/[0.06]" : "border-white/[0.08]"
            )}
          >
            <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground/60" />
            <input
              id="spectree-api-token"
              type="text"
              value={apiToken}
              onChange={(event) => {
                setApiToken(event.target.value);
              }}
              placeholder="st_..."
              className={cn(
                "w-full bg-transparent font-mono text-sm outline-none placeholder:text-muted-foreground/45",
                hasValidApiToken ? "text-emerald-400" : "text-foreground/80"
              )}
            />
            {hasValidApiToken ? <Check className="h-4 w-4 shrink-0 text-emerald-400" /> : null}
          </div>
        </div>

        {/* Copyable config block */}
        <div className="overflow-hidden rounded-xl border border-white/[0.08] bg-[#0D0D0F]">
          <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-2.5">
            <span className="text-xs font-medium text-muted-foreground">mcp-config.json</span>
            <button
              type="button"
              onClick={() => void copyConfig()}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-all",
                copiedConfig
                  ? "bg-emerald-500/10 text-emerald-400"
                  : "bg-white/[0.06] text-muted-foreground hover:bg-white/[0.1] hover:text-foreground"
              )}
            >
              <ClipboardCopy className="h-3 w-3" />
              {copiedConfig ? "Copied!" : "Copy"}
            </button>
          </div>
          <pre className="whitespace-pre-wrap px-4 py-3.5 font-mono text-[13px] leading-6 text-foreground/80">
            {configBlock}
          </pre>
        </div>

        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-400">
              <KeyRound className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-semibold text-emerald-300">
                Paste your token above for a ready-to-paste config
              </p>
              <p className="mt-1.5 text-sm leading-6 text-muted-foreground">
                Once your token starts with <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">st_</code>, the copied JSON already includes it. If you paste it in by hand, keep the quotes around the token.
              </p>
            </div>
          </div>
        </div>

        {/* Already have other MCP servers? */}
        <InfoCard>
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/[0.08] bg-violet-500/10 text-violet-400">
              <Settings className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">
                Already have other servers in this file?
              </p>
              <p className="mt-2 text-sm leading-7 text-muted-foreground">
                If this file already has other servers in it, do not replace the whole file. Add the <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">{'"spectree": { ... }'}</code> block inside the existing <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">{'"mcpServers"'}</code> section, with a comma after the item above it.
              </p>
            </div>
          </div>
        </InfoCard>

        {/* Save and done */}
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-5">
          <p className="text-sm font-semibold text-emerald-300">
            Save the file and you are done with this step.
          </p>
          <p className="mt-1.5 text-sm leading-6 text-muted-foreground">
            The next time you start Copilot CLI, it will find this file and connect to SpecTree. If Copilot CLI is already open, the new settings are picked up on the next command.
          </p>
        </div>

        <Troubleshooting>
          <div className="space-y-3 text-sm leading-6 text-muted-foreground">
            <p>
              <strong className="text-foreground/90">{"\"Invalid JSON\""} error?</strong>{" "}
              Make sure every opening brace has a matching closing brace and every text value uses double quotes. The most common mistake is a missing comma between items.
            </p>
            <p>
              <strong className="text-foreground/90">{"\"Unauthorized\""} when using SpecTree?</strong>{" "}
              Your token may be wrong or expired. Go back to Settings → API Tokens, make a fresh token, and paste it into the file again.
            </p>
          </div>
        </Troubleshooting>
      </NumberedInstructionLast>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Claude flow — install the spectree plugin, save the token with             */
/*  `claude mcp add --scope local`, then /spectree:spectree-setup              */
/* -------------------------------------------------------------------------- */

const buildMcpAddCommand = (token: string) =>
  QUICK_START_SETUP_COMMANDS.configureMcpServer.replace(
    "st_your_token",
    token.length > 0 ? token : MCP_TOKEN_PLACEHOLDER
  );

function CollapsibleFallback({ title, children }: { title: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-3">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          setOpen((current) => !current);
        }}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        {title}
      </button>
      {/* Rendered (but hidden) when collapsed so the fallback stays in the DOM for tests and find-in-page. */}
      <div
        hidden={!open}
        className="space-y-4 rounded-xl border border-white/[0.06] bg-white/[0.03] p-4"
      >
        {children}
      </div>
    </div>
  );
}

function ClaudeConfigureMcpFlow() {
  const [apiToken, setApiToken] = useState("");

  const trimmedApiToken = apiToken.trim();
  const hasValidApiToken = trimmedApiToken.startsWith("st_");
  const mcpAddCommand = buildMcpAddCommand(trimmedApiToken);
  const pluginInstallCommands = CLAUDE_PLUGIN_INSTALL_COMMANDS.join("\n");

  return (
    <div className="space-y-0">
      {/* ---- Step 1: What is MCP ---- */}
      <NumberedInstruction step={1} title="Understand what this step does">
        <p className="text-sm leading-7 text-muted-foreground">
          Right now, Claude Code and SpecTree do not know how to talk to each other. This step adds the bridge between them. That bridge is called <strong className="text-foreground/90">MCP</strong> (Model Context Protocol).
        </p>
        <InfoCard>
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/[0.08] bg-blue-500/10 text-blue-400">
              <Link2 className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">
                Think of MCP as a phone line
              </p>
              <p className="mt-2 text-sm leading-7 text-muted-foreground">
                Without it, Claude Code cannot ask SpecTree to do anything for you. Once you set it up, the two tools can pass messages back and forth.
              </p>
            </div>
          </div>
        </InfoCard>

        {/* Distinction callout */}
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-400">
              <Settings className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-semibold text-emerald-200">
                One plugin, your token, one setup command
              </p>
              <p className="mt-1.5 text-sm leading-6 text-muted-foreground">
                SpecTree&apos;s agents, skills, and safety hooks ship as the{" "}
                <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs text-foreground/90">spectree</code>{" "}
                Claude Code plugin, which updates itself. You save your token once from a terminal, then the plugin&apos;s{" "}
                <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs text-foreground/90">{CLAUDE_PLUGIN_SETUP_COMMAND}</code>{" "}
                command finishes the connection for this project in your own{" "}
                <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs text-foreground/90">~/.claude.json</code>{" "}
                — your token stays out of the repo.
              </p>
            </div>
          </div>
        </div>
      </NumberedInstruction>

      {/* ---- Step 2: Install the plugin ---- */}
      <NumberedInstruction step={2} title="Install the SpecTree plugin">
        <p className="text-sm leading-7 text-muted-foreground">
          Add these two keys to your project&apos;s{" "}
          <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">.claude/settings.json</code>{" "}
          (merge them into the file if it already exists). Everyone who opens the project in Claude Code and trusts the folder gets the plugin, and it updates automatically.
        </p>

        <CopyableTerminal title=".claude/settings.json" copyText={CLAUDE_PLUGIN_SETTINGS_SNIPPET}>
          <pre className="whitespace-pre-wrap font-mono text-[13px] leading-6 text-foreground/80">
            {CLAUDE_PLUGIN_SETTINGS_SNIPPET}
          </pre>
        </CopyableTerminal>

        <p className="text-sm leading-7 text-muted-foreground">
          Prefer not to change the repo? Install it for yourself only from a terminal:
        </p>
        <CopyableTerminal title="Terminal" copyText={pluginInstallCommands}>
          <pre className="whitespace-pre-wrap font-mono text-[13px] leading-6 text-foreground/80">
            {pluginInstallCommands}
          </pre>
        </CopyableTerminal>
      </NumberedInstruction>

      {/* ---- Step 3: Generate API token ---- */}
      <NumberedInstruction step={3} title="Generate your SpecTree API token">
        <p className="text-sm leading-7 text-muted-foreground">
          An API token is like a one-time password for apps. Claude Code uses it to prove it is acting for you when it talks to SpecTree. Here is how to make one.
        </p>

        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-500/15 text-[11px] font-bold text-violet-400">
              A
            </span>
            <div className="text-sm leading-7 text-muted-foreground">
              Open SpecTree in your browser and <strong className="text-foreground/90">sign in</strong>.
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-500/15 text-[11px] font-bold text-violet-400">
              B
            </span>
            <div className="text-sm leading-7 text-muted-foreground">
              Click on <strong className="text-foreground/90">Settings</strong> in the
              bottom dock. Look for the{" "}
              <span className="inline-flex items-center gap-1">
                <Settings className="inline h-3.5 w-3.5 text-muted-foreground/70" />
                <span className="font-medium text-foreground/90">gear icon</span>
              </span>.
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-500/15 text-[11px] font-bold text-violet-400">
              C
            </span>
            <div className="text-sm leading-7 text-muted-foreground">
              Click on the <strong className="text-foreground/90">API Tokens</strong> tab
              on the left side of the Settings page.
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-500/15 text-[11px] font-bold text-violet-400">
              D
            </span>
            <div className="text-sm leading-7 text-muted-foreground">
              Click <strong className="text-foreground/90">Generate New Token</strong>, give it a name like <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">Claude Code</code>, then click <strong className="text-foreground/90">Create</strong>.
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-500/15 text-[11px] font-bold text-violet-400">
              E
            </span>
            <div className="text-sm leading-7 text-muted-foreground">
              <strong className="text-foreground/90">Copy the token right away.</strong> It starts with <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">st_</code> and only shows once. If you close the page first, you will need to make a new one.
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-4">
          <label
            htmlFor="spectree-api-token-claude"
            className="text-sm font-medium text-foreground/90"
          >
            Paste your SpecTree API token
          </label>
          <div
            className={cn(
              "mt-3 flex items-center gap-3 rounded-lg border bg-white/[0.04] px-3.5 py-3 transition-all focus-within:border-violet-500/40 focus-within:ring-1 focus-within:ring-violet-500/30",
              hasValidApiToken ? "border-emerald-500/30 bg-white/[0.06]" : "border-white/[0.08]"
            )}
          >
            <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground/60" />
            <input
              id="spectree-api-token-claude"
              type="text"
              value={apiToken}
              onChange={(event) => {
                setApiToken(event.target.value);
              }}
              placeholder="st_..."
              className={cn(
                "w-full bg-transparent font-mono text-sm outline-none placeholder:text-muted-foreground/45",
                hasValidApiToken ? "text-emerald-400" : "text-foreground/80"
              )}
            />
            {hasValidApiToken ? <Check className="h-4 w-4 shrink-0 text-emerald-400" /> : null}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Paste your token here and the token command in the next step will already include it.
          </p>
        </div>
      </NumberedInstruction>

      {/* ---- Step 4: Save the token (setup can keep a token but cannot create one) ---- */}
      <NumberedInstruction step={4} title="Save your token for this project">
        <p className="text-sm leading-7 text-muted-foreground">
          Run this in a <strong className="text-foreground/90">terminal</strong> in your project folder, not in the Claude Code prompt. It saves your token for this project in your own{" "}
          <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">~/.claude.json</code>
          . Typing it in a terminal keeps the token out of your Claude Code chat history.
        </p>

        <CopyableTerminal title="Terminal" copyText={mcpAddCommand}>
          <pre className="whitespace-pre-wrap font-mono text-[13px] leading-6 text-foreground/80">
            {mcpAddCommand}
          </pre>
        </CopyableTerminal>

        <p className="text-sm leading-6 text-muted-foreground">
          Once your token starts with <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">st_</code>, the copied command already includes it. Otherwise replace <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">{MCP_TOKEN_PLACEHOLDER}</code> with your real token.
        </p>

        <CollapsibleFallback title="Already set up SpecTree in this project, or seeing “already exists”?">
          <p className="text-sm leading-6 text-muted-foreground">
            If SpecTree already works in this project, skip to step 5 — it keeps your saved token. If the existing{" "}
            <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">spectree</code>{" "}
            entry has no token or an old one, run{" "}
            <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">claude mcp remove spectree</code>{" "}
            first, then the command above again.
          </p>
        </CollapsibleFallback>
      </NumberedInstruction>

      {/* ---- Step 5: Run /spectree:spectree-setup ---- */}
      <NumberedInstructionLast step={5} title={`Finish with ${CLAUDE_PLUGIN_SETUP_COMMAND}`}>
        <p className="text-sm leading-7 text-muted-foreground">
          Start Claude Code in your project folder and run the command below in the Claude Code prompt. It installs the SpecTree MCP launcher in{" "}
          <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">~/.spectree/bin</code>{" "}
          (so SpecTree keeps working when your GitHub Packages token expires) and points the entry from step 4 at it. Your token is kept.
        </p>

        <CopyableTerminal title="Claude Code prompt" copyText={CLAUDE_PLUGIN_SETUP_COMMAND}>
          <pre className="whitespace-pre-wrap font-mono text-[13px] leading-6 text-foreground/80">
            {CLAUDE_PLUGIN_SETUP_COMMAND}
          </pre>
        </CopyableTerminal>

        <p className="text-sm leading-6 text-muted-foreground">
          No plugin? Skip this step — step 4 alone connects SpecTree, just without the launcher.
        </p>

        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-5">
          <p className="text-sm font-semibold text-emerald-300">
            That is it — restart Claude Code.
          </p>
          <p className="mt-1.5 text-sm leading-6 text-muted-foreground">
            The next time you start Claude Code in this project, it will connect to SpecTree. Run{" "}
            <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">/mcp</code>{" "}
            in the session to confirm the SpecTree tools are connected.
          </p>
        </div>

        <Troubleshooting>
          <div className="space-y-3 text-sm leading-6 text-muted-foreground">
            <p>
              <strong className="text-foreground/90">{"\"command not found: claude\""}?</strong>{" "}
              Claude Code is not installed yet. Finish the install step first, then re-run the command.
            </p>
            <p>
              <strong className="text-foreground/90">
                <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">{CLAUDE_PLUGIN_SETUP_COMMAND}</code>{" "}
                is not a known command?
              </strong>{" "}
              The plugin is not installed or enabled yet. Repeat step 2, then restart Claude Code. Step 4 alone still connects SpecTree without it.
            </p>
            <p>
              <strong className="text-foreground/90">Setup warned about a missing API_TOKEN?</strong>{" "}
              Step 4 was skipped or did not save. Run the step 4 command, then{" "}
              <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">{CLAUDE_PLUGIN_SETUP_COMMAND}</code>{" "}
              again.
            </p>
            <p>
              <strong className="text-foreground/90">{"\"Unauthorized\""} when using SpecTree?</strong>{" "}
              Your token may be wrong or expired. Go to Settings → API Tokens, make a fresh token, and run{" "}
              <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">claude mcp remove spectree</code>{" "}
              then steps 4 and 5 again.
            </p>
          </div>
        </Troubleshooting>
      </NumberedInstructionLast>
    </div>
  );
}
